import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { HardHat, CheckCircle2, Store } from "lucide-react";

export default function PartnerApply() {
  const { token } = useParams();
  const [state, setState] = useState("loading"); // loading | form | done | invalid | reviewed
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState({ name: "", category: "bonding", tagline: "", location: "", contact: "", website: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/marketplace/invite/${token}`)
      .then((r) => {
        setCats(r.data.categories || []);
        if (r.data.status === "sent" || r.data.status === "submitted") { setState("form"); setForm((f) => ({ ...f, category: r.data.categories?.[0]?.id || "bonding" })); }
        else setState("reviewed");
      })
      .catch(() => setState("invalid"));
  }, [token]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await api.post(`/marketplace/apply/${token}`, form);
      setState("done");
    } catch (e2) { setErr(e2?.response?.data?.detail || "Could not submit. Please try again."); setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-emerald-950 flex items-center justify-center p-4" data-testid="partner-apply-page">
      <div className="w-full max-w-lg">
        <div className="flex items-center justify-center gap-2 mb-6 text-white">
          <div className="h-9 w-9 rounded-lg bg-lime-400/20 flex items-center justify-center"><HardHat className="h-5 w-5 text-lime-300" strokeWidth={2.2} /></div>
          <span className="font-heading font-bold text-lg tracking-tight">BidMatchAI</span>
        </div>

        <div className="bg-white rounded-3xl shadow-2xl p-8">
          {state === "loading" && <div className="flex justify-center py-8"><div className="h-8 w-8 border-[3px] border-slate-200 border-t-emerald-700 rounded-full animate-spin" /></div>}

          {state === "invalid" && (
            <div className="text-center py-6" data-testid="apply-invalid">
              <h1 className="font-heading text-xl font-bold text-slate-900">Invite not found</h1>
              <p className="text-slate-500 mt-2 text-sm">This invite link is invalid or has expired. Please ask your contact for a new one.</p>
            </div>
          )}

          {state === "reviewed" && (
            <div className="text-center py-6" data-testid="apply-reviewed">
              <h1 className="font-heading text-xl font-bold text-slate-900">Already submitted</h1>
              <p className="text-slate-500 mt-2 text-sm">This application has already been submitted or reviewed.</p>
            </div>
          )}

          {state === "done" && (
            <div className="text-center py-6" data-testid="apply-done">
              <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto mb-4" />
              <h1 className="font-heading text-2xl font-bold text-slate-900">Application received!</h1>
              <p className="text-slate-500 mt-2">Thanks — the BidMatchAI team will review your listing and be in touch. You'll appear in the Partner Marketplace once approved.</p>
            </div>
          )}

          {state === "form" && (
            <>
              <div className="flex items-center gap-2 text-emerald-800 mb-1"><Store className="h-5 w-5" /><span className="text-xs font-bold uppercase tracking-wider">Partner Marketplace</span></div>
              <h1 className="font-heading text-2xl font-bold text-slate-900">Apply to join</h1>
              <p className="text-slate-500 mt-1 text-sm">Tell contractors what you offer. Submissions are reviewed before going live.</p>
              <form onSubmit={submit} className="mt-6 space-y-3">
                <input data-testid="apply-name" required placeholder="Company name" value={form.name} onChange={set("name")} className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/30" />
                <select data-testid="apply-category" value={form.category} onChange={set("category")} className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm">
                  {cats.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
                <textarea data-testid="apply-tagline" placeholder="Short description of your services" value={form.tagline} onChange={set("tagline")} rows={3} className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/30" />
                <input data-testid="apply-location" placeholder="Location (e.g. Houston, TX)" value={form.location} onChange={set("location")} className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/30" />
                <input data-testid="apply-contact" type="email" required placeholder="Contact email" value={form.contact} onChange={set("contact")} className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/30" />
                <input data-testid="apply-website" placeholder="Website (optional)" value={form.website} onChange={set("website")} className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/30" />
                {err && <p className="text-sm text-red-600" data-testid="apply-error">{err}</p>}
                <button type="submit" disabled={busy} data-testid="apply-submit" className="w-full bg-emerald-900 text-white rounded-xl py-3.5 text-sm font-semibold hover:bg-emerald-800 disabled:opacity-60 transition-colors">
                  {busy ? "Submitting…" : "Submit application"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
